import { chartBoxHorizontal } from '../../../constants/dashboard';
import { constants } from '../../../constants';
import { OnHoverTooltip, EmptyPlaceholder } from '../../common';
import { PxTalkNormalizationTable } from './PxTalkNormalizationTable'
import IconSVG from '../../../styles/svg-icons';
import { isRequestSuccess } from '../../../utils/request-state.utils';
import { Preloader } from '../Preloader';
import { usePriceTalks } from '../../../effects/usePriceTalks';
import { ValueBlocker } from '../../access/ValueBlocker';
import { HorizontalBoxChart } from '../charts/horizontalBox/HorizontalBoxChart';
import { ChartRequiredFeature } from '../../access/ChartRequiredFeature';
import { SubscriptionFeature } from '../../../types/billing/SubscriptionFeature';
import { RequiredFeature } from '../../access/RequiredFeature';
import { EvalKTalkLabel } from './EvalKTalkLabel';
import { useAppSelector } from '../../../effects/useAppSelector';
import { BankLink } from '../../amrPipeline/common/BankLink';
import { formatUtils } from '../../../utils';
import { Table } from '../../bidding/common/table';
import { IColumnDefinition } from '../../bidding/common/table/types/ColumnDefinition';
import { PxTalk } from '../../../types/bwic/PxTalk';
import { ColumnBuilder } from '../../bidding/common/table/columns/column-builder/ColumnBuilder';

const chartName = "Price Talk Dispersion";

interface PxTalkListProps {
    bwicReferenceName: string,
    isParsed: boolean,
    evalPriceTalkVisible?: boolean,
    evalPriceTalk?: string | null,
    normalizedEvalPriceTalk?: number | null
}

export function PxTalkList({
    bwicReferenceName,
    isParsed,
    evalPriceTalkVisible = false,
    evalPriceTalk = null,
    normalizedEvalPriceTalk = null
}: PxTalkListProps) {

    const visible = useAppSelector(store => store.pxTalkList.visible);
    const positionId = useAppSelector(store => store.pxTalkList.positionId);
    const priceTalksState = usePriceTalks(bwicReferenceName, positionId ?? 0);
    const priceTalks = positionId && visible ? priceTalksState.priceTalks : [];

    if (!visible || !positionId) {
        return null;
    }

    const renderEmptyList = () => {
        const placeholder = (
            <div className="px-talk-information-placeholder">
                <EmptyPlaceholder textView={true} text="No Px Talk submitted." />
            </div>
        );

        return (
            <div className="px-talk-information">
                {
                    isParsed ? (
                        <RequiredFeature feature={SubscriptionFeature.BwicMonitorBwicsData}>
                            {placeholder}
                        </RequiredFeature>
                    ) : placeholder
                }
            </div>
        );
    };

    const renderChart = () => {
        const chart = <HorizontalBoxChart
            pxTalks={priceTalks}
            evalPriceTalk={normalizedEvalPriceTalk}
            evalPriceTalkVisible={evalPriceTalkVisible}
            constants={chartBoxHorizontal}
        />;

        return isParsed ? (
            <ChartRequiredFeature
                feature={SubscriptionFeature.BwicMonitorBwicsData}
                chartName={chartName}
                blockedClassName="restricted-placeholder-px-talk"
            >
                {chart}
            </ChartRequiredFeature>
        ) : chart
    }

    const renderPriceTalkValue = (priceTalk?: string | null | number) => isParsed ? (
        <ValueBlocker feature={SubscriptionFeature.BwicMonitorBwicsData}>
            {priceTalk}
        </ValueBlocker>
    ) : priceTalk

    const pxTalks = priceTalks
        .filter(pxTalk => pxTalk.talk && pxTalk.company)
        .sort((a, b) => (b.normalizedLevel ?? 0) - (a.normalizedLevel ?? 0));

    const getColumns = () => {
        const columns: IColumnDefinition<PxTalk>[] = [{
            columnKey: "icon",
            renderColumnHeaderContent: () => "",
            renderColumnContent: (_, ctx: { rowIndex: number }) =>
                ctx.rowIndex === 0
                    ? <IconSVG name="kopentech" width={16} height={16} />
                    : <IconSVG name="on-platform" width={16} height={16} />,
            className: "data-list-cell-xxxs"
        }, {
            columnKey: "company",
            renderColumnHeaderContent: () => "Companies",
            renderColumnContent: (pxTalk, ctx: { rowIndex: number }) =>
                ctx.rowIndex === 0
                    ? <EvalKTalkLabel
                        withInfoButton={true}
                        withIcon={false}
                        light={true}
                        animated={false}
                    />
                    : <BankLink
                        referenceName={pxTalk.company.referenceName}
                        legalName={pxTalk.company.name}
                    />,
            headerClassName: "data-list-cell-md-flexible padding-l-0",
            bodyClassName: "data-list-cell-md-flexible padding-l-0 text-medium"
        }, {
            columnKey: "talk",
            renderColumnHeaderContent: () =>
                <>
                    Px Talk
                    <OnHoverTooltip
                        overlayClassName="px-talk-tooltip"
                        overlay={<PxTalkNormalizationTable />}
                    >
                        <IconSVG name="info" width={16} height={16} />
                    </OnHoverTooltip>
                </>,
            renderColumnContent: (pxTalk, ctx: { rowIndex: number }) =>
                ctx.rowIndex === 0
                    ? evalPriceTalk || constants.emptyPlaceholder
                    : renderPriceTalkValue(pxTalk.talk),
            headerClassName: "text-right",
            bodyClassName: "text-right text-bold"
        }, {
            columnKey: "digitized",
            renderColumnHeaderContent: () =>
                <>
                    Digitized
                    <OnHoverTooltip
                        overlayClassName="px-talk-tooltip"
                        overlay={<PxTalkNormalizationTable />}
                    >
                        <IconSVG name="info" width={16} height={16} />
                    </OnHoverTooltip>
                </>,
            renderColumnContent: (pxTalk, ctx: { rowIndex: number }) =>
                ctx.rowIndex === 0
                    ? normalizedEvalPriceTalk ? formatUtils.formatDecimal(normalizedEvalPriceTalk) : constants.emptyPlaceholder
                    : pxTalk.normalizedLevel
                        ? renderPriceTalkValue(formatUtils.formatDecimal(pxTalk.normalizedLevel))
                        : constants.emptyPlaceholder,
            headerClassName: "text-right",
            bodyClassName: "text-right text-bold"
        }]

        return columns.map(c => new ColumnBuilder(c));
    }

    const hasPxTalksToDisplay = (evalPriceTalkVisible && evalPriceTalk)
        || (pxTalks.length > 0 && pxTalks.some(p => p.normalizedLevel));

    return (
        <Preloader inProgress={!isRequestSuccess(priceTalksState.requestState)}>
            {
                isRequestSuccess(priceTalksState.requestState) &&
                <>
                    {
                        hasPxTalksToDisplay &&
                        <div className="px-talk-information" onClick={e => e.stopPropagation()}>
                            <div className="px-talk-information-title">
                                <h3>{chartName}</h3>
                            </div>
                            <div className="px-talk-information-chart">
                                {renderChart()}
                            </div>
                            <div className="px-talk-information-history">
                                <Table
                                    className="data-list-striped"
                                    dataItems={[{}].concat(pxTalks)}
                                    columns={getColumns()}
                                />
                            </div>
                        </div>
                    }
                    {!hasPxTalksToDisplay && renderEmptyList()}
                </>
            }
        </Preloader>
    );
}